import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"

export default class extends Controller {
  static targets = ["form", "locationId", "serviceType", "socialPreference", "petType",
                    "petsDisplay", "petsDog", "petsCat"]
  static values = { maxResults: Number }

  connect() {
    // Only auto-submit if this is the initial connection
    // and not a reconnection from turbo frame updates
    if (this.hasFormTarget && !this.hasSubmitted) {
      this.hasSubmitted = true
      // Skip auto-submit if no location selected
      if (this.hasLocationSelected()) {
        this.submitSearch()
      }
    }

    // Initialize social preference state based on service type selection
    this.updateSocialPreferenceState()
  }

  hasLocationSelected() {
    if (!this.hasLocationIdTarget) return false
    const value = this.locationIdTarget.value
    return value !== "" && value !== null && value !== undefined
  }

  selectValue(event) {
    const field = event.currentTarget.dataset.searchFormField
    const display = event.currentTarget.dataset.searchFormDisplay
    const value = event.currentTarget.dataset.searchFormValue
    const text = event.currentTarget.dataset.searchFormText

    // Find and update the hidden field by data attribute
    const hiddenField = this.element.querySelector(`[data-search-form-target="${field}"]`)
    if (hiddenField) {
      hiddenField.value = value
    }

    // Find and update the display field by data attribute
    const displayField = this.element.querySelector(`[data-search-form-target="${display}"]`)
    if (displayField) {
      displayField.value = text
    }

    // Auto-submit after location selection
    if (field === "locationId") {
      // Update datepicker closed dates for the new location
      const closedDates = event.currentTarget.dataset.searchFormClosedDates
      if (closedDates) {
        this.updateDatepickerClosedDates(closedDates)
      }

      this.submitSearch()
    }
  }

  updateDatepickerClosedDates(closedDatesJson) {
    // Find the datepicker element and update its unavailable dates value
    const datepickerElement = this.element.querySelector('[data-controller="datepicker"]')
    if (datepickerElement) {
      datepickerElement.dataset.datepickerUnavailableDatesValue = closedDatesJson
    }
  }

  updateFieldValue(fieldName, value) {
    const target = this[`${fieldName}Target`]
    if (target) {
      target.value = value
    }
  }

  // The Who pill shows the counts chosen inside it. The form is not re-rendered when the
  // results reload, so the summary has to be kept in step here or it goes stale.
  updatePets() {
    const dogs = parseInt(this.petsDogTarget.value, 10) || 0
    const cats = parseInt(this.petsCatTarget.value, 10) || 0

    const parts = []
    if (dogs > 0) parts.push(`${dogs} ${dogs === 1 ? "dog" : "dogs"}`)
    if (cats > 0) parts.push(`${cats} ${cats === 1 ? "cat" : "cats"}`)

    if (this.hasPetsDisplayTarget) {
      this.petsDisplayTarget.value = parts.length ? parts.join(", ") : "Add pets"
    }
    this.submitSearch()
  }

  async submitSearch(event = null) {
    if (event) event.preventDefault()
    
    // Get the form element
    // const form = event.target

    const form = this.formTarget

    const formData = new FormData(form)
    const params = new URLSearchParams(formData)
    
    // Get the action URL from the form
    const url = `${form.action}?${params.toString()}`
    
    try {
      // Make the request for turbo stream response
      const response = await get(url, {
        responseKind: "turbo-stream"
      })
      
      if (!response.ok) {
        console.error("Search failed:", response.statusText)
        // Could show error state here
      }
    } catch (error) {
      console.error("Search error:", error)
      // Could show error state here
    }
  }

  clearValue(event) {
    this.locationIdTarget.value = ""
    this.locationDisplayTarget.value = ""

    // Trigger form submission if needed
    this.element.requestSubmit()
  }

  updateSocialPreferenceState() {
    if (!this.hasSocialPreferenceTarget) return

    const checkedServiceTypes = this.hasServiceTypeTarget
      ? this.serviceTypeTargets.filter(cb => cb.checked).map(cb => cb.value)
      : []

    const checkedPetTypes = this.hasPetTypeTarget
      ? this.petTypeTargets.filter(cb => cb.checked).map(cb => cb.value)
      : []

    // Disable social preferences if ONLY daycare is selected OR ONLY cats selected
    const onlyDaycareSelected = checkedServiceTypes.length === 1 && checkedServiceTypes[0] === "daycare"
    const onlyCatsSelected = checkedPetTypes.length === 1 && checkedPetTypes[0] === "cat"
    const shouldDisable = onlyDaycareSelected || onlyCatsSelected

    this.socialPreferenceTargets.forEach(checkbox => {
      const label = checkbox.closest("label")
      if (shouldDisable) {
        checkbox.disabled = true
        checkbox.checked = false
        if (label) label.classList.add("opacity-40", "cursor-not-allowed")
      } else {
        checkbox.disabled = false
        if (label) label.classList.remove("opacity-40", "cursor-not-allowed")
      }
    })
  }

  // Service type is a MODE, not a filter: it changes which QUESTIONS the form asks, not
  // just which results come back. Daycare drops the dates field and the cats stepper —
  // days are chosen in the flow, and no resort sells cat daycare.
  //
  // submitSearch cannot deliver that. It fetches a turbo-stream that updates
  // `search_complete_results` only, so the form itself never re-renders and the dropped
  // fields stay on screen until a hard reload. That is exactly how it was reported:
  // "dates is still showing for daycare, as are cats".
  //
  // So this navigates instead. One rule, on the server, rendering the whole page — no
  // second copy of "what daycare asks" living in JavaScript to drift from the first.
  serviceTypeChanged() {
    this.updateSocialPreferenceState()

    const params = new URLSearchParams(new FormData(this.formTarget))
    window.location.assign(`${window.location.pathname}?${params.toString()}`)
  }

  petTypeChanged() {
    this.updateSocialPreferenceState()
    this.submitSearch()
  }
}
